import type { InsightType } from './insightType';
import type { SiteTrend } from './siteTrend';

type BaseSignal = { eventHappenedDaysAgo: number; insightType: InsightType.Signal; id: string; isDefault?: boolean };

export enum InsightSignalType {
  AdNetwork = 'adNetwork',
  Technology = 'technology',
  Traffic = 'traffic',
  News = 'news',
  Intent = 'intent',
}

export enum SignalType {
  AdNetwork = 'AdNetwork',
  Technology = 'Technology',
  Traffic = 'Traffic',
  News = 'News',
  Intent = 'Intent',
}

export enum NewsCategory {
  Hires = 'hires',
  Promotes = 'promotes',
  Leaves = 'leaves',
  Retires = 'retires_from',
  Acquires = 'acquires',
  MergesWith = 'merges_with',
  SellsAssetsTo = 'sells_assets_to',
  ExpandsOfficesTo = 'expands_offices_to',
  ExpandsOfficesIn = 'expands_offices_in',
  ExpandsFacilities = 'expands_facilities',
  OpensNewLocation = 'opens_new_location',
  IncreasesHeadcountBy = 'increases_headcount_by',
  Launches = 'launches',
  IntegratesWith = 'integrates_with',
  IsDeveloping = 'is_developing',
  ReceivesFinancing = 'receives_financing',
  InvestsInto = 'invests_into',
  InvestsIntoAssets = 'invests_into_assets',
  GoesPublic = 'goes_public',
  ClosesOffices = 'closes_offices_in',
  DecreasesHeadcountBy = 'decreases_headcount_by',
  PartnersWith = 'partners_with',
  SignsNewClient = 'signs_new_client',
  FilesSuitAgainst = 'files_suit_against',
  HasIssuesWith = 'has_issues_with',
  IdentifiedAsCompetitorOf = 'identified_as_competitor_of',
}

export enum NewsSignalSubType {
  Leadership = 'Leadership',
  Growth = 'Growth',
  Strategy = 'Strategy',
  Investment = 'Investment',
  CompanyChallenges = 'CompanyChallenges',
  AnyNews = 'AnyNews',
}

export type NewsSignal = BaseSignal & {
  type: InsightSignalType.News | SignalType.News;
  categories: NewsCategory[];
  url: string;
  articleSentence: string;
  title: string;
  jobTitle?: string;
  location?: string;
  relatedCompanyDomain?: string;
  product?: string;
  financingType?: string;
  amount?: string;
};

export enum AdNetworkChangeType {
  StartedWorkingWith = 'StartedWorkingWith',
  StoppedWorkingWith = 'StoppedWorkingWith',
}

export type AdNetworkSignal = BaseSignal & {
  type: InsightSignalType.AdNetwork | SignalType.AdNetwork;
  changeType: AdNetworkChangeType;
  adNetworkIds: number[];
  adNetworkNames: string[];
};

export enum TechnologyChangesType {
  Added = 'Added',
  Removed = 'Removed',
  Renew = 'Renew',
  RenewalComingUp = 'RenewalComingUp',
}

export enum TechnologySignalSubType {
  AddedTechnologies = 'AddedTechnologies',
  AddedIntegrations = 'AddedIntegrations',
  RemovedTechnologies = 'RemovedTechnologies',
  RemovedIntegrations = 'RemovedIntegrations',
  RemovedMyTechnologies = 'RemovedMyTechnologies',
}

export type TechnologySignal = BaseSignal & {
  changeType: TechnologyChangesType;
  technologies: string[];
  periodDays?: number;
  type: InsightSignalType.Technology | SignalType.Technology;
  subType?: TechnologySignalSubType;
};

export type TrafficChangesPeriod = 'mom' | 'qoq' | 'yoy';

export const trafficChangesPeriodInMonths: Record<TrafficChangesPeriod, number> = {
  mom: 1,
  qoq: 3,
  yoy: 12,
};

export type TrafficChangeDirection = 'grew' | 'declined';

export type TrafficChangesFilter = {
  trafficChanges: {
    country: number;
    direction: TrafficChangeDirection;
    metric: string;
    period: TrafficChangesPeriod;
    value: number;
  };
};

export type NewMarketsFilter = {
  newMarkets: {
    countries: number[];
    metric: string;
    thresholdValue: number;
  };
};

type BaseTrafficSignal = BaseSignal & {
  type: InsightSignalType.Traffic | SignalType.Traffic;
  changeValue: number;
  subType: 'Month' | 'Year';
  countries: number[];
  siteTrends: Omit<SiteTrend, 'insightType' | 'id'>[];
};

export type TrafficChangesSignal = BaseTrafficSignal & {
  filters: TrafficChangesFilter;
};

export type NewMarketsSignal = BaseTrafficSignal & {
  filters: NewMarketsFilter;
};

export type IntentCountryDto = {
  country: string;
  city: string;
  countryCode: number;
  compositeScore: number;
  compositeScoreDelta: number;
};

export type IntentSignal = BaseSignal & {
  type: SignalType.Intent;
  events: {
    topic: string;
    countriesScopes: readonly IntentCountryDto[];
    compositeScore: number;
    compositeScoreDelta: number;
  }[];
};

export enum LeadPriorityScore {
  High = 'high',
  Medium = 'medium',
  Low = 'low',
  Unlock = 'unlock',
  NoScore = 'noScore',
}

export type LeadPriority = { score: LeadPriorityScore; triggeredSignals: readonly Signal[] };

export type TrafficSignal = TrafficChangesSignal | NewMarketsSignal;
export type Signal = NewsSignal | AdNetworkSignal | TechnologySignal | TrafficSignal | IntentSignal;
export type SignalsResponse = { items: Signal[]; leadPriority: LeadPriority | null };
